import { useState } from "react";
import CMSTeaser from "~/features/cms/components/cmsTeaser/CMSTeaser";
import { type CMSTeaserProps } from "~/features/cms/types/model";
import { NARROW, WIDE } from "~/utils/constants";
import { CMSSection } from "~/features/cms/components/cmsSection/CMSSection";
import type { CMSSectionComponentProps } from "~/features/cms/types/props";
import type ComponentTypes from "~/features/cms/componentTypes";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { CMSTeaserContainer } from "~/features/cms/components/cmsTeaser/CMSTeaserContainer";
import { type CMSFilterableTileContainerFilterDTO } from "~/features/cms/components/cmsFilterableTile/dto";
import { useTileFilter } from "~/features/cms/components/cmsFilterableTile/useTileFilter";
import { makeExternallyStyledBaseElement } from "~/features/cms/utils/styles";
import { styled } from "@schaeffler-commerce/design-system";
import { C_SURFACE } from "@schaeffler-commerce/design-system";
import CloseIcon from "@mui/icons-material/Close";

export enum DesktopContainerTypes {
  DEFAULT = "DEFAULT",
  CAROUSEL = "CAROUSEL",
}

export enum MobileContainerTypes {
  DEFAULT = "DEFAULT",
  HORIZONTAL = "HORIZONTAL_SCROLL",
  CAROUSEL = "CAROUSEL",
}

export interface CMSTeasersProps {
  id: string;
  type: ComponentTypes.CMSTeaserContainer;
  teasers: CMSTeaserProps[];
  scrollType?: DesktopContainerTypes;
  itemsPerRow?: number;
  mobileScrollType?: MobileContainerTypes;
  mobileItemsPerRow?: number;
  className?: string;
  filters?: CMSFilterableTileContainerFilterDTO[];
  showAllLabel?: string;
}

export const CMSTeasers = ({
  scrollType = DesktopContainerTypes.DEFAULT,
  itemsPerRow = 1,
  mobileScrollType = MobileContainerTypes.DEFAULT,
  mobileItemsPerRow = 1,
  teasers,
  filters = [],
  className,
  showAllLabel,
}: CMSTeasersProps) => {
  const isDesktop = useIsMinDesktopSize();

  const toggle = (index: number) => setOpenIndex((prevValue) => (index === prevValue ? null : index));
  const closeContent = () => setOpenIndex(null);

  let wideness: typeof WIDE | typeof NARROW;
  if (isDesktop) {
    wideness = itemsPerRow > 2 ? NARROW : WIDE;
  } else {
    wideness = NARROW;
  }
  const renderItem = (item: CMSTeaserProps) => (
    <CMSTeaser
      key={item.index}
      {...item}
      setContent={(content) => setContent({ content, index: content ? item.index : -1 })}
      onToggle={toggle}
      isExpanded={isTeaserExpanded(item.index)}
      wideness={wideness}
    />
  );

  // TODO: Temporary code, need to assign index, wrong type is coming above
  const tiles: CMSTeaserProps[] = teasers.map((teaser, index) => ({ ...teaser, index }));

  const { filterContainer, visibleTiles } = useTileFilter({ filters, showAllLabel, allTiles: tiles });

  const [{ content, index: contentIndex }, setContent] = useState<{
    content: CMSSectionComponentProps | null;
    index: number;
  }>({
    content: null,
    index: -1,
  });
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const isTeaserExpanded = (index: number) => index === openIndex;

  const renderTeaserDetailsContainer = (validIds: number[]) => {
    if (openIndex === null || !validIds.includes(openIndex)) {
      return null;
    }

    return (
      <OpenDetailsLayer
        className={"scha__teasers__container scha__teasers__container--active"}
        rootStyles={content?.rootStyles}
        key={contentIndex}
      >
        <TeasersCloseButton onClick={closeContent}>
          <CloseIcon sx={{ fontSize: 24 }} />
        </TeasersCloseButton>
        {content && (
          <TeasersTarget fullWidth={content?.fullWidth === true}>
            <CMSSection {...content} rootStyles={undefined} />
          </TeasersTarget>
        )}
      </OpenDetailsLayer>
    );
  };

  return (
    <div className={className}>
      {filterContainer}

      <CMSTeaserContainer
        scrollType={scrollType}
        itemsPerRow={itemsPerRow}
        mobileScrollType={mobileScrollType}
        mobileItemsPerRow={mobileItemsPerRow}
        className={className}
        teasers={visibleTiles}
        renderTeaser={renderItem}
        renderTeaserDetailsContainer={renderTeaserDetailsContainer}
      />
    </div>
  );
};

// Migrated from CMSTeasers.less (.scha__teasers__container). The --active
// position:fixed variant stays keyed on the container classes in
// CMSTeaserContainer's styled root (it only applies in --HORIZONTAL mode).
const OpenDetailsLayer = styled(makeExternallyStyledBaseElement("div"))({
  marginTop: "32px",
  marginBottom: "16px",
  position: "relative",
  borderRadius: "12px",
  backgroundColor: C_SURFACE,
});

// Migrated from CMSTeasers.less (.scha__teasers__x).
const TeasersCloseButton = styled("div")({
  position: "absolute",
  top: "16px",
  right: "16px",
  cursor: "pointer",
});

// Migrated from CMSTeasers.less (.scha__teasers__target / __target__full) plus
// the CMS Section exceptions inside the teaser dropdown. The headline rules
// targeted .scha__cmssection__headline, which no renderer applies (dead); the
// sub-element headline reset stays because CMSList keeps the marker class.
const TeasersTarget = styled("div", { shouldForwardProp: (prop) => prop !== "fullWidth" })<{
  fullWidth?: boolean;
}>(({ fullWidth }) => ({
  ...(fullWidth
    ? {
        maxWidth: "100%",
        margin: 0,
        padding: "16px",
      }
    : {
        margin: "16px auto",
        maxWidth: "928px",
        padding: "16px 32px",
      }),
  "& .scha__cmssection__sub-element__headline": {
    marginTop: 0,
  },
}));
